import React, { useCallback, useMemo, useState } from "react";
import type { RowSelectionState } from "@tanstack/react-table";
import { z } from "zod";
import { formatISO } from "date-fns";
import { Counterparty } from "@common/types/counterparty";
import { DirectorySort } from "@common/constants/counterparty";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    CounterpartyDirectoryView,
    CounterpartyDirectoryViewSchema,
} from "@common/types/views/counterpartyDirectoryView";
import { View } from "@common/types/views/views";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { DirectoryFilters } from "./lib";
import { DirectoryContext, GlDrawerState } from "./Directory.context";
import { useCounterpartyDirectoryViewConfig } from "./useCounterpartyDirectoryViewConfig";
import { CounterpartyDirectoryViewRow } from "./counterpartyDirectoryViewTypes";

interface CachedFilters {
    year?: number;
    startDate?: string;
    endDate?: string;
    entityIds?: number[];
    basis?: AccountingLedgerBasis;
}

interface Props {
    children: React.ReactNode;
}

export const DirectoryContextProvider: React.FC<Props> = ({ children }) => {
    const { defaultView } = useCounterpartyDirectoryViewConfig();
    const [addingNew, setAddingNew] = useState(false);
    const [editedItem, setEditedItem] = useState<Counterparty>();
    const [selectedRow, setSelectedRow] =
        useState<CounterpartyDirectoryViewRow | null>(null);
    const [glDrawerState, setGlDrawerState] = useState<GlDrawerState | null>(
        null,
    );
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const clearRowSelection = useCallback(() => {
        setRowSelection({});
    }, []);
    const [cachedFilters, setCachedFilters] =
        useWorkspaceUserCache<CachedFilters>(
            WORKSPACE_USER_CACHE_KEYS.DIRECTORY_FILTERS_CACHE_KEY,
            z.object({
                year: z.number().optional(),
                startDate: z.string().date().optional(),
                endDate: z.string().date().optional(),
                entityIds: z.array(z.number()).optional(),
                basis: z.nativeEnum(AccountingLedgerBasis).optional(),
            }),
        );
    const [filters, setFilters] = useState<DirectoryFilters>(() => ({
        startDate:
            cachedFilters?.startDate ??
            (cachedFilters?.year
                ? formatISO(new Date(cachedFilters.year, 0, 1), {
                      representation: "date",
                  })
                : undefined),
        endDate:
            cachedFilters?.endDate ??
            (cachedFilters?.year
                ? formatISO(new Date(cachedFilters.year, 11, 31), {
                      representation: "date",
                  })
                : undefined),
        entityIds: cachedFilters?.entityIds,
        basis: cachedFilters?.basis ?? AccountingLedgerBasis.cash,
    }));
    const [counterpartySort, setCounterpartySort] = useState<DirectorySort>(
        DirectorySort.JOURNAL_ENTRY_LINES_DESC,
    );
    const [cachedView, setCachedView] =
        useWorkspaceUserCache<CounterpartyDirectoryView>(
            WORKSPACE_USER_CACHE_KEYS.COUNTERPARTY_DIRECTORY_PAGE_VIEW_CACHE_KEY,
            CounterpartyDirectoryViewSchema,
        );
    const [view, setView] = useState<CounterpartyDirectoryView>(
        cachedView ?? defaultView,
    );

    const updateFilters = useCallback(
        (newFilters: DirectoryFilters) => {
            setFilters(newFilters);
            setSelectedRow(null);
            setRowSelection({});
            setCachedFilters({
                startDate: newFilters.startDate,
                endDate: newFilters.endDate,
                entityIds: newFilters.entityIds,
                basis: newFilters.basis,
            });
        },
        [setCachedFilters],
    );

    const onViewChange = useCallback(
        (newView: View) => {
            const typedView = newView as CounterpartyDirectoryView;
            setView(typedView);
            setCachedView(typedView);
        },
        [setCachedView],
    );

    const openGlDrawer = useCallback(
        (
            counterpartyId: string | null,
            isNoCounterparty: boolean,
            accountCode?: number,
        ) => {
            setGlDrawerState({ counterpartyId, isNoCounterparty, accountCode });
        },
        [],
    );

    const closeGlDrawer = useCallback(() => {
        setGlDrawerState(null);
    }, []);

    const value = useMemo(
        () => ({
            addingNew,
            setAddingNew,
            filters,
            setFilters: updateFilters,
            editedItem,
            setEditedItem,
            counterpartySort,
            setCounterpartySort,
            view,
            onViewChange,
            selectedRow,
            setSelectedRow,
            glDrawerState,
            openGlDrawer,
            closeGlDrawer,
            rowSelection,
            setRowSelection,
            clearRowSelection,
        }),
        [
            addingNew,
            filters,
            editedItem,
            updateFilters,
            counterpartySort,
            view,
            onViewChange,
            selectedRow,
            glDrawerState,
            openGlDrawer,
            closeGlDrawer,
            rowSelection,
            clearRowSelection,
        ],
    );

    return (
        <DirectoryContext.Provider value={value}>
            {children}
        </DirectoryContext.Provider>
    );
};
